import { useEffect } from "react";
import { useQuery } from "@apollo/client/react";
import { PublicCartProductsDocument } from "@/generated/graphql";
import { mergeValidatedCartItems } from "@/lib/cartValidation";
import { useCart } from "@/contexts/CartContext";

export interface CartHydratorProps {
  storeSlug: string;
}

export default function CartHydrator({ storeSlug }: CartHydratorProps) {
  const { isHydrating, pendingProductIds, pendingValidation, hydrateCart, isReady } =
    useCart();

  const productIds = isHydrating ? pendingProductIds : [];

  const { data, loading, error } = useQuery(PublicCartProductsDocument, {
    variables: { storeSlug, productIds },
    skip: !isHydrating || productIds.length === 0,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (!isHydrating || isReady) return;

    if (productIds.length === 0) {
      hydrateCart([]);
      return;
    }

    if (loading) return;

    if (error) {
      hydrateCart([]);
      return;
    }

    const pending = pendingValidation ?? [];
    const products = data?.publicCartProducts ?? [];
    hydrateCart(mergeValidatedCartItems(pending, products));
  }, [
    isHydrating,
    isReady,
    loading,
    error,
    data,
    productIds.length,
    pendingValidation,
    hydrateCart,
  ]);

  return null;
}
